import type { Laufergebnis, Laufzeile } from '@crm/kontrakte/src/hintergrundlauf';

import { rufe } from './client';

export type { Laufergebnis, Laufzeile };

/**
 * Der Zustand eines Umsystems (ERR-15).
 *
 * Vier Werte, und der vierte ist der wichtigste für die Anzeige:
 * `unbekannt` heißt „noch nie geprüft" und ist bewusst kein „nicht
 * erreichbar". Ein frisch neu gestartetes System, das alles rot meldet,
 * schickt jemanden los, bevor überhaupt gemessen wurde.
 */
export type Umsystemzustand = 'erreichbar' | 'gestoert' | 'nicht_erreichbar' | 'unbekannt';

export type Umsystemstand = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly wesentlich: boolean;
  readonly zustand: Umsystemzustand;
  readonly meldung: string | null;
  /**
   * ERR-15: Zeitpunkt der letzten ERFOLGREICHEN Verbindung — nicht der
   * letzten Prüfung. Er bleibt bei einer Störung stehen und sagt damit, wie
   * lange sie schon dauert.
   */
  readonly letzteVerbindung: string | null;
  readonly geprueftAm: string | null;
};

/** Die Beschriftung je Zustand. Sie steht hier, damit sie überall gleich lautet. */
export const ZUSTANDSNAME: Readonly<Record<Umsystemzustand, string>> = {
  erreichbar: 'erreichbar',
  gestoert: 'gestört',
  nicht_erreichbar: 'nicht erreichbar',
  unbekannt: 'noch nicht geprüft',
};

/**
 * Die Zustandsmarke je Wert — der Namensteil hinter `marke--` in stil.css.
 *
 * `gestoert` bekommt die ACHTUNG-Farbe und nicht die FEHLER-Farbe: Der Dienst
 * lebt und sagt nein — für den, der am Tresen steht, ist das etwas anderes
 * als Funkstille, und die Abhilfe ist eine andere.
 *
 * **`warn` und `krit`, nicht `achtung` und `fehler`.** Die Farben heißen so,
 * die Klassen nicht: stil.css führt `.marke--warn` und `.marke--krit`. Hier
 * standen die Farbnamen, und weil die Klasse erst in der Maske
 * zusammengesetzt wird, sah die Prüfung „jede benutzte Klasse hat eine Regel"
 * sie nie — zwei der vier Zustände wurden ohne Farbe gezeichnet. Wer diese
 * Werte ändert, ändert Klassennamen; `gestaltung.test.ts` rechnet sie nach.
 */
export const ZUSTANDSMARKE: Readonly<Record<Umsystemzustand, string>> = {
  erreichbar: 'gut',
  gestoert: 'warn',
  nicht_erreichbar: 'krit',
  unbekannt: 'inaktiv',
};

/** `hintergrund` für die Abrufe im Takt: Sie halten die Sitzung nicht wach (BEN-07). */
export async function stand(hintergrund = false): Promise<readonly Umsystemstand[]> {
  const antwort = await rufe<{ umsysteme: readonly Umsystemstand[] }>('/status', { hintergrund });
  return antwort.umsysteme;
}

/**
 * Die Kennzahlen der Anlage (BET-22, NFA-16, BP-41).
 *
 * Sie stehen neben dem Zustand der Umsysteme, weil beide dieselbe Frage
 * beantworten: geht es der Anlage gut? Wer bei einer Störung nachsieht, will
 * nicht zwischen zwei Masken wechseln, um zu merken, dass die Ablage voll ist.
 *
 * **Keine Beträge.** `umsystem:sehen` haben auch Empfang und Operations; eine
 * Zeilenzahl trägt keinen Namen und keinen Umsatz. Summen gehören hinter
 * `auswertung:sehen` (AUS-06).
 */
export type Systemkennzahlen = {
  readonly stand: string;
  readonly laeuftSeit: string;
  readonly datenbank: {
    readonly groesseBytes: number;
    readonly groesse: string;
    readonly verbindungen: number;
    readonly tabellen: readonly {
      readonly name: string;
      readonly groesseBytes: number;
      readonly groesse: string;
      /** Geschätzt aus dem Katalog — die Anzeige sagt es dazu. */
      readonly zeilenGeschaetzt: number;
    }[];
  };
  /** Exakt gezählt — darauf verlässt sich jemand. */
  readonly bestand: readonly { readonly was: string; readonly anzahl: number }[];
  /**
   * Das Protokoll (LOG-11, LOG-12).
   *
   * Beide Zahlen sind billig gehalten: `protokoll.aenderung` ist die einzige
   * Tabelle, die unbegrenzt wächst, und ein genaues Zählen kostete dort bei
   * jedem Aufruf der Seite eine Viertelsekunde. Statt des ältesten Eintrags
   * steht deshalb der letzte Fristenlauf da — er beantwortet die nützlichere
   * Frage: läuft das Aufräumen überhaupt?
   */
  readonly protokoll: {
    readonly eintraegeGeschaetzt: number;
    readonly letzterLauf: string | null;
    readonly letzterLaufErgebnis: string | null;
  };
};

export async function kennzahlen(hintergrund = false): Promise<Systemkennzahlen> {
  // BEN-07: im Takt mit `hintergrund`.
  return rufe('/status/kennzahlen', { hintergrund });
}

/**
 * Das Verzeichnis der Hintergrundläufe (LOG-09): Takt, letzte Runde,
 * Ergebnis und Fehler in Folge je Lauf. Dasselbe Recht wie der Systemzustand.
 */
export async function laeufe(hintergrund = false): Promise<readonly Laufzeile[]> {
  const antwort = await rufe<{ laeufe: readonly Laufzeile[] }>('/status/laeufe', { hintergrund });
  return antwort.laeufe;
}

/** Die Beschriftung und Marke je Ergebnis; `null` heißt: noch keine Runde. */
export const LAUFERGEBNIS: Readonly<Record<Laufergebnis, { readonly name: string; readonly marke: string }>> = {
  erfolg: { name: 'erfolgreich', marke: 'gut' },
  fehler: { name: 'gescheitert', marke: 'krit' },
  uebersprungen: { name: 'übersprungen', marke: 'inaktiv' },
};
